import React, { Fragment, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import { VoucherFilters } from "./components/VoucherFilters";
import { VoucherTable } from "./components/VoucherTable";
import { VoucherDetailDialog } from "./components/VoucherDetailDialog";
import { VoucherConfirmationDialog } from "./components/VoucherConfirmationDialog";
import { VoucherEntryDialog } from "./components/VoucherEntryDialog";
import { FilterSidebar } from "../../components/FilterSidebar";
import { useBranch } from "../../components/BranchContext";
import { useVouchers } from "./hooks/useVouchers";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";

const voucherStatusTabs = [
  {
    value: "",
    label: "All",
    color: "#6B7280",
    icon: "list_alt",
    description: "All vouchers",
  },
  {
    value: "Posted",
    label: "Posted",
    color: "#10B981",
    icon: "check_circle",
    description: "Posted vouchers",
  },
  {
    value: "Draft",
    label: "Draft",
    color: "#F59E0B",
    icon: "edit",
    description: "Draft vouchers",
  },
  {
    value: "Reversed",
    label: "Reversed",
    color: "#EF4444",
    icon: "undo",
    description: "Reversed vouchers",
  },
];

export const VouchersPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const {
    // State
    filters,
    selectedVoucher,
    isDetailDialogOpen,
    isFormDialogOpen,
    isLoading,
    tableData,
    confirmationDialog,

    // Handlers
    handleFiltersChange,
    handleResetFilters,
    handleApplyFilters,
    handleVoucherClick,
    handleCloseDetailDialog,
    handleOpenFormDialog,
    handleCloseFormDialog,
    handleEditVoucher,
    handleReverseVoucher,
    handleDeleteVoucher,
    handlePostVoucher,
    handleSubmitVoucher,
    handleCopyVoucherId,
    handleDownloadPDF,
    handlePageChange,
    handlePageSizeChange,
    handleCloseConfirmationDialog,
    handleConfirmAction,
    page,
    rowsPerPage,
    filteredVouchers,
  } = useVouchers();

  // Wrapper function to handle the new voucher entry dialog
  const handleVoucherSave = React.useCallback(
    async (voucherData: Partial<import("./types").VoucherFormData>) => {
      // Convert VoucherLineForm[] to the format expected by the API
      const transformedVoucherLines = (voucherData.voucherLines || []).map(
        (line) => ({
          account_id: line.account?.id || "",
          account_code: line.account?.accountCode || undefined,
          account_name: line.account?.accountName || undefined,
          debit: line.debit || 0,
          credit: line.credit || 0,
          description: line.description || undefined,
        })
      );

      // Convert the partial VoucherFormData to full format expected by the hook
      const formData = {
        voucherType: voucherData.voucherType || "payment",
        voucherDate:
          voucherData.voucherDate || new Date().toISOString().split("T")[0],
        narration: voucherData.narration || "",
        beneficiary: voucherData.beneficiary,
        linkedEntity: voucherData.linkedEntity,
        paymentMethod: voucherData.paymentMethod,
        referenceNumber: voucherData.referenceNumber,
        branchId: voucherData.branchId,
        voucherLines: transformedVoucherLines,
      };

      await handleSubmitVoucher(formData);
    },
    [handleSubmitVoucher]
  );

  // Sync branch context with local filters when branch changes
  React.useEffect(() => {
    if (filters.selectedBranch !== selectedBranch) {
      handleFiltersChange({ selectedBranch });
    }
  }, [selectedBranch, filters.selectedBranch, handleFiltersChange]);

  // Handle status tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const selectedStatus = voucherStatusTabs[newValue]?.value;
    const statusValue = (selectedStatus === "" ? "all" : selectedStatus) as
      | "draft"
      | "posted"
      | "reversed"
      | "all";
    handleFiltersChange({ status: statusValue });
  };

  // Handle search query changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    handleFiltersChange({ narrationContains: query });
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "selectedBranch") return false; // Exclude branch as it's handled globally
      if (key === "status") return false; // Exclude status as it's handled by tabs
      if (key === "narrationContains") return false; // Exclude search as it's handled by search bar
      return value && value.toString().trim() !== "";
    }).length;
  };

  const handleToggleFilterSidebar = () => {
    setIsFilterSidebarOpen(!isFilterSidebarOpen);
  };

  const getConfirmationDialogProps = () => {
    const { type, voucher } = confirmationDialog;

    switch (type) {
      case "reverse":
        return {
          title: "Reverse Voucher",
          message: `Are you sure you want to reverse voucher ${voucher?.voucher_id}? This will create a new reversing voucher with swapped debits and credits.`,
          confirmText: "Reverse Voucher",
          severity: "warning" as const,
          details: [
            "A new voucher will be created with reversed debits and credits",
            "Both vouchers will remain in the system for audit purposes",
            "The reversing voucher will be automatically posted",
          ],
        };
      case "delete":
        return {
          title: "Delete Voucher",
          message: `Are you sure you want to delete voucher ${voucher?.voucher_id}? This action cannot be undone.`,
          confirmText: "Delete Voucher",
          severity: "error" as const,
          details: [
            "This will permanently remove the voucher",
            "Only draft vouchers can be deleted",
            "This action cannot be undone",
          ],
        };
      case "post":
        return {
          title: "Post Voucher",
          message: `Are you sure you want to post voucher ${voucher?.voucher_id}? This will create a journal entry and the voucher cannot be edited afterwards.`,
          confirmText: "Post Voucher",
          severity: "info" as const,
          details: [
            "A journal entry will be automatically created",
            "The voucher status will change to Posted",
            "Posted vouchers cannot be edited, only reversed",
          ],
        };
      default:
        return {
          title: "",
          message: "",
          confirmText: "",
          severity: "info" as const,
          details: [],
        };
    }
  };

  const confirmationProps = getConfirmationDialogProps();

  return (
    <Fragment>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
            >
              Vouchers
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontSize: "1rem" }}
            >
              Structured money movement transactions and auto-journal entries
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleOpenFormDialog}
            disabled={
              !hasPermission(
                "accounting.vouchers.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.5,
            }}
          >
            New Voucher
          </Button>
        </Box>

        {/* Status Tabs & Search */}
        <Box
          sx={{
            mb: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 64,
                px: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {voucherStatusTabs.map((statusTab, index) => (
              <Tab
                key={statusTab.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: statusTab.color,
                      }}
                    />
                    <Icon sx={{ fontSize: 18 }}>{statusTab.icon}</Icon>
                    {statusTab.label}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {/* Search & Filters */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3 }}>
            <TextField
              size="small"
              placeholder="Search vouchers..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                minWidth: 280,
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.background.default, 0.7),
                  },
                  "&.Mui-focused": {
                    bgcolor: alpha(theme.palette.background.default, 0.9),
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
                      search
                    </Icon>
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Advanced Filters">
              <IconButton
                onClick={handleToggleFilterSidebar}
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor:
                    getActiveFiltersCount() > 0
                      ? "primary.main"
                      : "transparent",
                  color: getActiveFiltersCount() > 0 ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor:
                      getActiveFiltersCount() > 0
                        ? "primary.dark"
                        : "action.hover",
                  },
                }}
              >
                <Icon>tune</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: "max-content",
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            p: 0,
            position: "relative",
          }}
        >
          <VoucherTable
            data={tableData}
            pagination={{
              total: filteredVouchers.length,
              page: page,
              pageSize: rowsPerPage,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            onVoucherClick={handleVoucherClick}
            onReverseVoucher={handleReverseVoucher}
            onDeleteVoucher={handleDeleteVoucher}
            onEditVoucher={handleEditVoucher}
            onPostVoucher={handlePostVoucher}
            onCopyVoucherId={handleCopyVoucherId}
            onDownloadPDF={handleDownloadPDF}
          />
        </Box>
      </Box>

      {/* Filter Sidebar */}
      <FilterSidebar
        open={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        title="Voucher Filters"
        activeFiltersCount={getActiveFiltersCount()}
        onResetFilters={handleResetFilters}
        onApplyFilters={() => {
          handleApplyFilters();
          setIsFilterSidebarOpen(false);
        }}
      >
        <VoucherFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onResetFilters={handleResetFilters}
          onApplyFilters={handleApplyFilters}
        />
      </FilterSidebar>

      {/* Voucher Detail Dialog */}
      <VoucherDetailDialog
        open={isDetailDialogOpen}
        onClose={handleCloseDetailDialog}
        voucher={selectedVoucher}
        onReverse={handleReverseVoucher}
        onEdit={handleEditVoucher}
        onDelete={handleDeleteVoucher}
        onPost={handlePostVoucher}
        onDownloadPDF={handleDownloadPDF}
      />

      {/* Voucher Entry Dialog */}
      <VoucherEntryDialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleVoucherSave}
        isLoading={isLoading}
      />

      {/* Confirmation Dialog */}
      <VoucherConfirmationDialog
        open={confirmationDialog.open}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmAction}
        title={confirmationProps.title}
        message={confirmationProps.message}
        confirmText={confirmationProps.confirmText}
        severity={confirmationProps.severity}
        isLoading={isLoading}
        details={confirmationProps.details}
        voucher={confirmationDialog.voucher}
      />
    </Fragment>
  );
};
